import { useMemo, useState } from 'react';
import { CATEGORIES, FEED_ITEMS, HOT_RANK } from '@/mocks/entertainment';
import type { FeedCategory } from '@/mocks/entertainment';

type Tab = FeedCategory | '全部';

const CATEGORY_META: Record<FeedCategory, { icon: string; desc: string }> = {
  现场直击: { icon: 'ri-vidicon-line', desc: '活动实录 · 偶遇路透 · 舞台直拍' },
  红毯生图: { icon: 'ri-camera-line', desc: '原图直出 · 妆造前后台状态' },
  综艺名场面: { icon: 'ri-emotion-laugh-line', desc: '接梗神回 · 后台人情味片段' },
  热剧动态: { icon: 'ri-movie-2-line', desc: '定档 · 首播 · 口碑与彩蛋盘点' },
};

export default function ContentZone() {
  const [tab, setTab] = useState<Tab>('全部');

  const list = useMemo(() => {
    if (tab === '全部') {
      const sorted = [...FEED_ITEMS].sort((a, b) => Number(b.featured ?? false) - Number(a.featured ?? false));
      return sorted;
    }
    return FEED_ITEMS.filter((i) => i.category === tab);
  }, [tab]);

  const showMeta = tab !== '全部' ? CATEGORY_META[tab] : { icon: 'ri-sparkling-2-line', desc: '各频道资讯实时聚合' };

  return (
    <section id="live" className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_330px]">
        {/* ---------- LEFT FEED ---------- */}
        <div className="min-w-0">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="font-label text-sm tracking-[0.3em] text-primary-600">WATCH &amp; FOLLOW</p>
              <h3 className="mt-2 font-heading text-3xl font-black text-foreground-950">资讯现场</h3>
              <p className="mt-1.5 flex items-center gap-1.5 text-sm text-foreground-600">
                <i className={`${showMeta.icon} text-primary-500`}></i> {showMeta.desc}
              </p>
            </div>
            <span className="hidden shrink-0 items-center gap-1.5 text-xs text-foreground-500 sm:flex">
              <i className="ri-radio-2-line text-primary-500"></i> 全站已累计 <b className="text-foreground-900">2,431</b> 条现场记录
            </span>
          </div>

          {/* tabs */}
          <div className="mt-6 flex flex-wrap gap-2" role="tablist" aria-label="资讯分类切换">
            {(['全部', ...CATEGORIES] as Tab[]).map((t) => (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={tab === t}
                onClick={() => setTab(t)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                  tab === t
                    ? 'bg-foreground-950 text-background-50'
                    : 'border border-background-200 bg-background-100/70 text-foreground-700 hover:border-foreground-400/50 hover:text-foreground-950'
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          {/* feed list */}
          <div className="mt-6 flex flex-col divide-y divide-background-200">
            {list.map((item) => (
              <article key={item.id} className="group flex flex-col gap-4 py-5 sm:flex-row">
                <a href="#live" className="relative block h-44 w-full shrink-0 overflow-hidden rounded-2xl sm:h-32 sm:w-44" aria-label={item.title}>
                  <img
                    src={item.image}
                    alt={`${item.title} 现场生图与相关配图`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  {item.featured && (
                    <span className="absolute left-2 top-2 rounded-full bg-accent-500 px-2 py-0.5 text-[10px] font-bold text-foreground-950">
                      热门
                    </span>
                  )}
                </a>
                <div className="flex min-w-0 flex-1 flex-col">
                  <div className="flex items-center gap-2">
                    <span className="rounded-md bg-primary-100 px-2 py-0.5 text-[11px] font-bold text-primary-700">{item.category}</span>
                    <span className="rounded-md bg-secondary-100 px-2 py-0.5 text-[11px] font-medium text-secondary-800">{item.tag}</span>
                  </div>
                  <a href="#live" className="mt-2 line-clamp-2 font-heading text-lg font-bold leading-snug text-foreground-950 transition-colors hover:text-primary-700 md:text-xl">
                    {item.title}
                  </a>
                  <p className="mt-1.5 line-clamp-2 text-sm leading-6 text-foreground-600">{item.excerpt}</p>
                  <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
                    <span className="inline-flex items-center gap-1"><i className="ri-edit-line"></i>{item.author}</span>
                    <span className="inline-flex items-center gap-1"><i className="ri-time-line"></i>{item.date}</span>
                    <span className="inline-flex items-center gap-1"><i className="ri-eye-line"></i>{item.views} 浏览</span>
                    <span className="ml-auto hidden items-center gap-1 font-semibold text-primary-600 sm:inline-flex">
                      看现场 <i className="ri-arrow-right-s-line"></i>
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {list.length === 0 && (
            <p className="rounded-2xl border border-dashed border-background-300 py-10 text-center text-sm text-foreground-500">
              该频道内容整理中，先看看其他栏目吧
            </p>
          )}
        </div>

        {/* ---------- RIGHT SIDEBAR ---------- */}
        <aside id="hot" className="min-w-0 space-y-6 lg:sticky lg:top-36 lg:self-start">
          {/* hot ranking */}
          <div className="rounded-3xl border border-background-200 bg-background-100/60 p-5">
            <div className="flex items-center justify-between">
              <h4 className="font-heading text-xl font-black text-foreground-950">瓜田热榜</h4>
              <span className="inline-flex items-center gap-1 rounded-full bg-primary-600 px-2.5 py-1 text-[10px] font-bold text-background-50">
                <i className="ri-fire-line"></i> 实时
              </span>
            </div>
            <p className="mt-1 text-xs text-foreground-600">过去 24 小时最受关注的瓜点</p>
            <ol className="mt-4 space-y-1.5">
              {HOT_RANK.map((r) => (
                <li key={r.no}>
                  <a href="#live" className="group flex items-start gap-3 rounded-xl px-1.5 py-2 transition-colors hover:bg-background-50">
                    <span
                      className={`font-label w-6 shrink-0 text-center text-xl leading-none ${
                        r.no <= 3 ? 'text-primary-600' : 'text-foreground-400'
                      }`}
                    >
                      {String(r.no).padStart(2, '0')}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="line-clamp-2 text-[13px] font-semibold leading-5 text-foreground-900 group-hover:text-primary-700">
                        {r.title}
                      </span>
                      <span className="mt-1 flex items-center gap-2 text-[11px] text-foreground-500">
                        <span className="rounded bg-secondary-100 px-1.5 text-secondary-800">{r.cat}</span>
                        <span className="inline-flex items-center gap-0.5 text-primary-600"><i className="ri-fire-fill"></i>{r.heat}</span>
                      </span>
                    </span>
                  </a>
                </li>
              ))}
            </ol>
          </div>

          {/* mini subscribe card */}
          <div className="overflow-hidden rounded-3xl bg-foreground-950 p-5 text-background-50">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-accent-500 text-foreground-950">
              <i className="ri-mail-star-line text-lg"></i>
            </span>
            <h4 className="mt-3 font-heading text-lg font-black">怕错过名场面？</h4>
            <p className="mt-1.5 text-[13px] leading-6 text-background-100/80">
              订阅「吃瓜速递」，重要场次直播预告与名场面合集第一时间送达你的邮箱。
            </p>
            <a
              href="#subscribe"
              className="mt-4 inline-flex items-center justify-center whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-400"
            >
              立即订阅
            </a>
          </div>

          {/* topic tags */}
          <div className="rounded-3xl border border-background-200 bg-background-50 p-5">
            <h4 className="font-heading text-base font-black text-foreground-950">常逛话题</h4>
            <div className="mt-3 flex flex-wrap gap-2">
              {['红毯生图', '路透现场', '舞台直拍', '综艺梗', '剧集口碑', '后台花絮', '音乐节', '见面会'].map((t) => (
                <a
                  key={t}
                  href="#live"
                  className="rounded-full border border-background-200 bg-background-100/70 px-3 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
                >
                  # {t}
                </a>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}